<script setup>
import FixPanel from "@/components/approval-center/partials/ui/FixPanel.vue";
import FixPanelHeader from "@/components/approval-center/partials/ui/FixPanelHeader.vue";
import FixPanelHeaderTitle from "@/components/approval-center/partials/ui/FixPanelHeaderTitle.vue";
import MoveGroupTransition from "@/components/transitions/MoveGroupTransition.vue";
import FixPanelIcon from "@/components/approval-center/partials/ui/FixPanelIcon.vue";
import FixContent from "@/components/approval-center/partials/ui/FixContent.vue";
import FixText from "@/components/approval-center/partials/ui/FixText.vue";
import KitIcon from "@/components/approval-center/partials/kit/KitIcon.vue";
import SuccessFlow from "@/components/approval-center/modals/SuccessFlow.vue";
import useApprovalFlow from "@/composables/approval-center/useApprovalFlow";
import RevisionFlow from "@/components/approval-center/modals/RevisionFlow.vue";
import {useApprovals} from "@/composables/approval-center/useApprovals";
import LoadingBlock from "@/components/global-themed/LoadingBlock.vue";
import LoadMoreButton from "@/components/approval-center/partials/buttons/LoadMoreButton.vue";
import FixPanelSubText from "@/components/approval-center/partials/ui/FixPanelSubText.vue";
import SupportingKeywordModal from "@/components/approval-center/partials/SupportingKeywordModal.vue";
import SupportingKeywords from "@/components/approval-center/partials/ui/SupportingKeywords.vue";
import FixPanelFooter from "@/components/approval-center/partials/ui/FixPanelFooter.vue";

const props = defineProps({
  category: {
    type: String,
    required: true,
  },
});

const {
  showSuccessFlow,
  selectedApproval,
  handleConfirmMutation,
  handleClose,
  selectApprovalForApprove,
  selectApprovalForRevision,
  showRevisionFlow,
  revisionText,
  handleConfirmRevisionMutation,
  showKeywordsModal,
  openKeywordsModal,
  closeKeywordsModal,
} = useApprovalFlow();

const {mutate: handleConfirm} = handleConfirmMutation();
const {mutate: handleRevisionMutate, isPending: isRevisionPending} = handleConfirmRevisionMutation();

const {
  approvals,
  isPending,
} = useApprovals(props.category);
</script>

<template>
  <FixPanel v-if="isPending || approvals.length">
    <FixPanelHeader class="tw-flex-col !tw-items-start">
      <FixPanelHeaderTitle>Blog Posts</FixPanelHeaderTitle>
      <FixPanelSubText>Check out the blog posts I’ve written to drive more traffic—please review and let me know what you think. Just a reminder, you’ll need to upload the posts to your website yourself.</FixPanelSubText>
    </FixPanelHeader>

    <LoadingBlock v-if="isPending" />

    <MoveGroupTransition
      v-else
      class="tw-space-y-8"
    >
      <FixPanel
        v-for="approval in approvals"
        :key="approval.id"
      >
        <FixPanelHeader>
          <FixPanelIcon icon-path="icons/notebook" />
          <FixPanelHeaderTitle>{{ approval.approvable.title }}</FixPanelHeaderTitle>
        </FixPanelHeader>

        <SupportingKeywords
          :approval="approval"
          @open="openKeywordsModal(approval)"
        />

        <FixContent class="tw-max-h-[360px] tw-prose tw-max-w-none tw-flex tw-items-start tw-gap-8">
          <KitIcon />

          <FixText>
            <div v-html="approval.approvable.content" />
          </FixText>
        </FixContent>

        <FixPanelFooter
          :approval="approval"
          :category="category"
          @approve="selectApprovalForApprove(approval)"
          @revise="selectApprovalForRevision(approval)"
        />
      </FixPanel>
    </MoveGroupTransition>
    <LoadMoreButton :category="category" />
  </FixPanel>
  <SuccessFlow
    v-if="showSuccessFlow"
    @confirm="handleConfirm({
      approval: selectedApproval,
      category,
    })"
    @close="handleClose"
  />
  <RevisionFlow
    v-if="showRevisionFlow"
    v-model:feedback="revisionText"
    :loading="isRevisionPending"
    @confirm="handleRevisionMutate({
      approval: selectedApproval,
      feedback: revisionText,
      category,
    })"
    @close="handleClose"
    @continue="handleClose"
  />
  <SupportingKeywordModal
    v-if="showKeywordsModal"
    @close="closeKeywordsModal"
  />
</template>
